import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { recognitionService } from '@/services/recognitionService';

const campaignsRef = (schoolId) => (
  collection(db, 'schools', schoolId, 'campaigns')
);

const campaignRef = (schoolId, campaignId) => (
  doc(db, 'schools', schoolId, 'campaigns', campaignId)
);

const nominationsRef = (schoolId, campaignId) => (
  collection(db, 'schools', schoolId, 'campaigns', campaignId, 'nominations')
);

const nominationRef = (schoolId, campaignId, nominationId) => (
  doc(db, 'schools', schoolId, 'campaigns', campaignId, 'nominations', nominationId)
);

const responsesRef = (schoolId, campaignId) => (
  collection(db, 'schools', schoolId, 'campaigns', campaignId, 'responses')
);

const responseRef = (schoolId, campaignId, responseId) => (
  doc(db, 'schools', schoolId, 'campaigns', campaignId, 'responses', responseId)
);

export const CAMPAIGN_ACTION_TYPES = {
  NOMINATE: 'nominate_learner',
  RECOGNISE: 'recognise_learner',
  ENTRY: 'submit_entry',
  CONFIRM: 'confirm',
};

const asDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isCampaignLive = (campaign, now = new Date()) => {
  if (campaign?.status !== 'active') return false;
  const startsAt = asDate(campaign.startsAt);
  const endsAt = asDate(campaign.endsAt);
  if (startsAt && startsAt > now) return false;
  if (endsAt && endsAt < now) return false;
  return true;
};

const legacyNominationAction = campaign => ({
  id: 'nomination',
  type: CAMPAIGN_ACTION_TYPES.NOMINATE,
  label: campaign.banner?.actionLabel || 'Nominate a learner',
  instructions: campaign.competition?.instructions || '',
  audienceRoles: campaign.audienceRoles || ['teacher', 'admin'],
  deadline: campaign.endsAt || null,
  allowCorrection: true,
  status: campaign.competition?.status === 'complete' ? 'closed' : 'open',
  recognition: campaign.recognition || null,
  order: 0,
  legacy: true,
});

export const getCampaignActions = campaign => {
  if (campaign?.actions?.length) {
    return [...campaign.actions].sort((a, b) => (a.order || 0) - (b.order || 0));
  }
  return campaign?.competition ? [legacyNominationAction(campaign)] : [];
};

export const isCampaignActionAvailable = (
  campaign,
  action,
  currentRole,
  now = new Date()
) => {
  if (!isCampaignLive(campaign, now) || action?.status === 'closed') return false;
  const deadline = asDate(action?.deadline || campaign?.endsAt);
  if (deadline && deadline < now) return false;
  return !action?.audienceRoles?.length || action.audienceRoles.includes(currentRole);
};

export const buildActionRules = actions => Object.fromEntries(actions.map(action => [
  action.id,
  {
    type: action.type,
    status: action.status || 'open',
    audienceRoles: action.audienceRoles || [],
    deadline: action.deadline || null,
    allowCorrection: Boolean(action.allowCorrection),
  },
]));

const normalizeAction = (action, campaign) => ({
  id: action.id,
  type: action.type,
  label: action.label?.trim(),
  instructions: action.instructions?.trim() || '',
  audienceRoles: action.audienceRoles?.length
    ? action.audienceRoles
    : campaign.audienceRoles,
  deadline: action.deadline || campaign.endsAt || null,
  allowCorrection: Boolean(action.allowCorrection),
  status: action.status === 'closed' ? 'closed' : 'open',
  order: Number(action.order) || 0,
  required: Boolean(action.required),
  textRequired: Boolean(action.textRequired),
  allowAttachments: action.allowAttachments !== false,
  maxFiles: Math.min(Math.max(Number(action.maxFiles) || 5, 1), 10),
  recognition: action.recognition?.valueName ? {
    valueId: action.recognition.valueId || action.recognition.valueName,
    valueName: action.recognition.valueName.trim(),
    reason: action.recognition.reason?.trim() || '',
  } : null,
});

const validateActions = actions => {
  const ids = new Set();
  actions.forEach(action => {
    if (!action.id || ids.has(action.id)) throw new Error('Every campaign action needs a unique id');
    ids.add(action.id);
    if (!Object.values(CAMPAIGN_ACTION_TYPES).includes(action.type)) {
      throw new Error('Choose a supported campaign action type');
    }
    if (!action.label) throw new Error('Give every campaign action a label');
    if (!action.audienceRoles?.length) throw new Error(`Choose an audience for ${action.label}`);
    if (
      action.type === CAMPAIGN_ACTION_TYPES.RECOGNISE &&
      !action.recognition?.valueName
    ) {
      throw new Error(`Choose the recognition for ${action.label}`);
    }
  });
};

const mapSnapshot = (snapshot) => (
  snapshot.docs.map(item => ({ id: item.id, ...item.data() }))
);

export function subscribeCampaigns(schoolId, callback, onError = console.error) {
  if (!schoolId) {
    callback([]);
    return () => {};
  }

  return onSnapshot(
    campaignsRef(schoolId),
    (snapshot) => {
      const campaigns = mapSnapshot(snapshot).sort((a, b) => {
        const aTime = asDate(a.startsAt)?.getTime() || 0;
        const bTime = asDate(b.startsAt)?.getTime() || 0;
        return bTime - aTime;
      });
      callback(campaigns);
    },
    onError
  );
}

export function subscribeActiveCampaigns(schoolId, callback, onError = console.error) {
  if (!schoolId) {
    callback([]);
    return () => {};
  }

  const activeQuery = query(campaignsRef(schoolId), where('status', '==', 'active'));
  return onSnapshot(
    activeQuery,
    (snapshot) => {
      const campaigns = mapSnapshot(snapshot)
        .sort((a, b) => {
          const aTime = asDate(a.startsAt)?.getTime() || 0;
          const bTime = asDate(b.startsAt)?.getTime() || 0;
          return aTime - bTime;
        });
      callback(campaigns);
    },
    onError
  );
}

export function subscribeNominations(
  schoolId,
  campaignId,
  callback,
  onError = console.error
) {
  if (!schoolId || !campaignId) {
    callback([]);
    return () => {};
  }

  return onSnapshot(
    nominationsRef(schoolId, campaignId),
    (snapshot) => {
      const nominations = mapSnapshot(snapshot).sort((a, b) => {
        const aTime = asDate(a.createdAt)?.getTime() || 0;
        const bTime = asDate(b.createdAt)?.getTime() || 0;
        return bTime - aTime;
      });
      callback(nominations);
    },
    onError
  );
}

export function subscribeCampaignResponses(
  schoolId,
  campaignId,
  callback,
  onError = console.error
) {
  if (!schoolId || !campaignId) {
    callback([]);
    return () => {};
  }

  return onSnapshot(
    responsesRef(schoolId, campaignId),
    snapshot => {
      const responses = mapSnapshot(snapshot).sort((a, b) => {
        const aTime = asDate(a.updatedAt || a.createdAt)?.getTime() || 0;
        const bTime = asDate(b.updatedAt || b.createdAt)?.getTime() || 0;
        return bTime - aTime;
      });
      callback(responses);
    },
    onError
  );
}

export async function createCampaign(schoolId, campaign, user) {
  if (!schoolId) throw new Error('A school is required');
  if (!user?.uid) throw new Error('You must be signed in');
  if (!campaign.title?.trim()) throw new Error('Campaign name is required');
  if (!campaign.banner?.message?.trim()) throw new Error('Banner message is required');
  const startsAt = asDate(campaign.startsAt);
  const endsAt = asDate(campaign.endsAt);
  if (!startsAt || !endsAt) throw new Error('Campaign start and end times are required');
  if (endsAt <= startsAt) throw new Error('Campaign end time must be after its start time');
  if (!campaign.audienceRoles?.length) throw new Error('Choose a campaign audience');

  const suppliedActions = campaign.actions?.length
    ? campaign.actions
    : campaign.competition ? [{
      id: 'nomination',
      type: CAMPAIGN_ACTION_TYPES.NOMINATE,
      label: campaign.banner?.actionLabel || 'Nominate a learner',
      instructions: campaign.competition?.instructions || '',
      audienceRoles: campaign.audienceRoles,
      deadline: campaign.endsAt,
      allowCorrection: true,
      status: 'open',
      order: 0,
      recognition: campaign.recognition || null,
    }] : [];
  const actions = suppliedActions.map(action => normalizeAction(action, campaign));
  validateActions(actions);
  const primaryActionId = actions.some(action => action.id === campaign.primaryActionId)
    ? campaign.primaryActionId
    : actions[0]?.id || null;
  const primaryAction = actions.find(action => action.id === primaryActionId);

  return addDoc(campaignsRef(schoolId), {
    title: campaign.title.trim(),
    status: campaign.status === 'active' ? 'active' : 'draft',
    startsAt: campaign.startsAt || null,
    endsAt: campaign.endsAt || null,
    audienceRoles: campaign.audienceRoles,
    banner: {
      headline: campaign.banner.headline?.trim() || campaign.title.trim(),
      message: campaign.banner.message.trim(),
      dateLabel: campaign.banner.dateLabel?.trim() || '',
      details: campaign.banner.details?.trim() || '',
      learnMoreLabel: campaign.banner.learnMoreLabel?.trim() || 'Learn more',
      actionLabel: primaryAction?.label || '',
      resultMessage: campaign.banner.resultMessage?.trim() || '',
      tone: campaign.banner.tone || 'success',
      imageUrl: campaign.banner.imageUrl || '',
      imageAlt: campaign.banner.imageAlt?.trim() || '',
      imageStoragePath: campaign.banner.imageStoragePath || '',
      positionX: Number.isFinite(Number(campaign.banner.positionX)) ? Number(campaign.banner.positionX) : 50,
      positionY: Number.isFinite(Number(campaign.banner.positionY)) ? Number(campaign.banner.positionY) : 50,
      zoom: Number.isFinite(Number(campaign.banner.zoom)) ? Number(campaign.banner.zoom) : 100,
    },
    competition: campaign.competition ? {
      label: campaign.competition?.label?.trim() || 'Competition',
      instructions: campaign.competition?.instructions?.trim() || '',
      entryType: 'learner_nomination',
      status: 'open',
    } : null,
    recognition: campaign.recognition?.valueName ? {
      valueId: campaign.recognition.valueId || campaign.recognition.valueName.trim(),
      valueName: campaign.recognition.valueName.trim(),
      reason: campaign.recognition.reason?.trim() || '',
    } : null,
    actions,
    actionRules: buildActionRules(actions),
    primaryActionId,
    winner: null,
    winnerSelection: null,
    schoolId,
    createdById: user.uid,
    createdByName: user.displayName || user.email || 'Administrator',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateCampaign(schoolId, campaignId, updates) {
  if (!schoolId || !campaignId) throw new Error('Campaign context is required');
  const nextUpdates = { ...updates };
  if (updates.actions) {
    validateActions(updates.actions);
    nextUpdates.actionRules = buildActionRules(updates.actions);
  }
  await updateDoc(campaignRef(schoolId, campaignId), {
    ...nextUpdates,
    updatedAt: serverTimestamp(),
  });
}

const responseIdFor = (actionId, userId) => (
  `${actionId}__${userId}`.replaceAll('/', '_')
);

const learnerSnapshot = student => {
  const name = (
    student.name ||
    `${student.firstName || ''} ${student.surname || student.lastName || ''}`
  ).trim();
  return {
    studentId: student.id,
    studentName: name,
    classId: student.classId || null,
    className: student.className || '',
    photoURL: student.photoURL || null,
  };
};

const validateResponsePayload = (action, payload) => {
  if (
    [CAMPAIGN_ACTION_TYPES.NOMINATE, CAMPAIGN_ACTION_TYPES.RECOGNISE].includes(action.type) &&
    !payload.student?.id
  ) {
    throw new Error('Choose a learner');
  }
  if (
    action.type === CAMPAIGN_ACTION_TYPES.ENTRY &&
    action.textRequired &&
    !payload.text?.trim()
  ) {
    throw new Error('Add the required response');
  }
  if (
    action.type === CAMPAIGN_ACTION_TYPES.ENTRY &&
    !payload.text?.trim() &&
    !payload.attachments?.length
  ) {
    throw new Error('Add a response or attachment');
  }
};

const responseData = (schoolId, campaign, action, payload, user, currentRole, existing) => {
  const base = {
    actionId: action.id,
    actionType: action.type,
    actionLabel: action.label,
    campaignId: campaign.id,
    schoolId,
    status: 'complete',
    submittedById: user.uid,
    submittedByName: user.displayName || user.email || 'Staff member',
    submittedByRole: currentRole || '',
    createdAt: existing?.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (action.type === CAMPAIGN_ACTION_TYPES.NOMINATE) {
    return {
      ...base,
      ...learnerSnapshot(payload.student),
      text: payload.text?.trim() || '',
      attachments: [],
      status: 'submitted',
    };
  }
  if (action.type === CAMPAIGN_ACTION_TYPES.ENTRY) {
    return {
      ...base,
      text: payload.text?.trim() || '',
      attachments: payload.attachments || [],
      status: 'submitted',
    };
  }
  if (action.type === CAMPAIGN_ACTION_TYPES.CONFIRM) {
    return {
      ...base,
      text: payload.text?.trim() || '',
      attachments: [],
      confirmed: true,
      confirmedAt: serverTimestamp(),
    };
  }
  return { ...base, ...learnerSnapshot(payload.student), text: '', attachments: [] };
};

export async function submitCampaignResponse({
  schoolId,
  campaign,
  action,
  payload,
  user,
  currentRole,
}) {
  if (!schoolId || !campaign?.id || !action?.id) {
    throw new Error('Campaign action context is required');
  }
  if (!user?.uid) throw new Error('You must be signed in');
  if (!isCampaignActionAvailable(campaign, action, currentRole)) {
    throw new Error('This action is closed or unavailable');
  }
  validateResponsePayload(action, payload);

  const refToResponse = responseRef(
    schoolId,
    campaign.id,
    responseIdFor(action.id, user.uid)
  );

  await runTransaction(db, async transaction => {
    const snapshot = await transaction.get(refToResponse);
    const existing = snapshot.exists() ? snapshot.data() : null;
    if (existing && !action.allowCorrection) {
      throw new Error('This action has already been completed');
    }
    transaction.set(
      refToResponse,
      responseData(schoolId, campaign, action, payload, user, currentRole, existing)
    );
  });

  return responseIdFor(action.id, user.uid);
}

export async function issueCampaignActionRecognition({
  schoolId,
  campaign,
  action,
  student,
  user,
  currentRole,
}) {
  if (!action?.recognition?.valueName) {
    throw new Error('This action has no recognition configured');
  }
  if (!student?.id) throw new Error('Choose a learner');
  if (!isCampaignActionAvailable(campaign, action, currentRole)) {
    throw new Error('This action is closed or unavailable');
  }

  const id = responseIdFor(action.id, user.uid);
  const refToResponse = responseRef(schoolId, campaign.id, id);
  const learner = learnerSnapshot(student);

  await runTransaction(db, async transaction => {
    const snapshot = await transaction.get(refToResponse);
    const existing = snapshot.exists() ? snapshot.data() : null;
    if (existing?.status === 'complete' || existing?.status === 'issuing') {
      throw new Error('This recognition has already been issued');
    }
    transaction.set(refToResponse, {
      actionId: action.id,
      actionType: action.type,
      actionLabel: action.label,
      campaignId: campaign.id,
      schoolId,
      ...learner,
      status: 'issuing',
      submittedById: user.uid,
      submittedByName: user.displayName || user.email || 'Staff member',
      submittedByRole: currentRole || '',
      createdAt: existing?.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  try {
    const recognition = await recognitionService.issueRecognition({
      ...learner,
      values: [{
        id: action.recognition.valueId || action.recognition.valueName,
        name: action.recognition.valueName,
      }],
      valueId: action.recognition.valueId || action.recognition.valueName,
      value: action.recognition.valueName,
      valueName: action.recognition.valueName,
      reason: action.recognition.reason || action.label || campaign.title,
      campaignId: campaign.id,
      campaignTitle: campaign.title,
      campaignActionId: action.id,
      campaignResponseId: id,
      issuedById: user.uid,
      issuedByName: user.displayName || user.email || 'Staff member',
      schoolId,
    });
    await updateDoc(refToResponse, {
      status: 'complete',
      recognitionId: recognition.id,
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return recognition;
  } catch (error) {
    await updateDoc(refToResponse, {
      status: 'failed',
      errorCode: error.code || 'recognition-failed',
      updatedAt: serverTimestamp(),
    });
    throw error;
  }
}

export async function createNomination(schoolId, campaign, student, reason, user) {
  if (!schoolId || !campaign?.id) throw new Error('Campaign context is required');
  if (!student?.id) throw new Error('Choose a learner');
  if (!user?.uid) throw new Error('You must be signed in');

  const safeId = `${user.uid}__${student.id}`.replaceAll('/', '_');
  const fullName = (
    student.name ||
    `${student.firstName || ''} ${student.surname || student.lastName || ''}`
  ).trim();

  await setDoc(nominationRef(schoolId, campaign.id, safeId), {
    studentId: student.id,
    studentName: fullName,
    classId: student.classId || null,
    className: student.className || '',
    photoURL: student.photoURL || null,
    reason: reason?.trim() || '',
    status: 'nominated',
    nominatedById: user.uid,
    nominatedByName: user.displayName || user.email || 'Staff member',
    campaignId: campaign.id,
    schoolId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return safeId;
}

export async function selectCampaignWinner(schoolId, campaign, nomination, user) {
  if (!schoolId || !campaign?.id || !nomination?.id) {
    throw new Error('Campaign and nomination are required');
  }
  if (!user?.uid) throw new Error('You must be signed in');

  const cRef = campaignRef(schoolId, campaign.id);
  const nRef = nominationRef(schoolId, campaign.id, nomination.id);

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(cRef);
    if (!snapshot.exists()) throw new Error('Campaign not found');
    const current = snapshot.data();

    if (current.winner?.recognitionId || current.winnerSelection?.status === 'complete') {
      throw new Error('A winner has already been selected');
    }
    if (current.winnerSelection?.status === 'issuing') {
      throw new Error('Winner selection is already in progress');
    }

    transaction.update(cRef, {
      winnerSelection: {
        status: 'issuing',
        nominationId: nomination.id,
        startedById: user.uid,
        startedAt: new Date(),
      },
      updatedAt: serverTimestamp(),
    });
  });

  let recognition;
  try {
    recognition = await recognitionService.issueRecognition({
      studentId: nomination.studentId,
      studentName: nomination.studentName,
      classId: nomination.classId || null,
      className: nomination.className || '',
      values: [{
        id: campaign.recognition.valueId || campaign.recognition.valueName,
        name: campaign.recognition.valueName,
      }],
      valueId: campaign.recognition.valueId || campaign.recognition.valueName,
      value: campaign.recognition.valueName,
      valueName: campaign.recognition.valueName,
      reason: campaign.recognition.reason || nomination.reason || campaign.title,
      campaignId: campaign.id,
      campaignTitle: campaign.title,
      nominationId: nomination.id,
      issuedById: user.uid,
      issuedByName: user.displayName || user.email || 'Administrator',
      schoolId,
    });

    const batch = writeBatch(db);
    batch.update(cRef, {
      winner: {
        nominationId: nomination.id,
        studentId: nomination.studentId,
        studentName: nomination.studentName,
        className: nomination.className || '',
        recognitionId: recognition.id,
        selectedById: user.uid,
        selectedByName: user.displayName || user.email || 'Administrator',
        selectedAt: new Date(),
      },
      winnerSelection: {
        status: 'complete',
        nominationId: nomination.id,
        recognitionId: recognition.id,
      },
      'competition.status': 'complete',
      updatedAt: serverTimestamp(),
    });
    batch.update(nRef, {
      status: 'winner',
      recognitionId: recognition.id,
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
    return recognition;
  } catch (error) {
    await updateDoc(cRef, {
      winnerSelection: {
        status: 'failed',
        nominationId: nomination.id,
      },
      updatedAt: serverTimestamp(),
    });
    throw error;
  }
}
